import { useInscalar } from '@ilg-core/inscalar-web/react'
import { useCallback } from 'react'

import {
    getFeatureFlagsForEvent,
    mapCardScoredProductToInscalarTrackedProduct,
    mapCardAltProductToInscalarTrackedProduct,
} from '@mediaingenuity/totallymoney.cardloanutils'
import {
    InscalarEventType,
    InscalarTrackPayload,
    InscalarTrackedCard,
} from '@mediaingenuity/totallymoney.types'
import Cookies from 'js-cookie'

import { ScoredCreditCardProduct } from '@appTypes/EligibilityApi.interface'
import {
    InscalarEventAction,
    InscalarEventBase,
    InscalarEventClickout,
    InscalarEventClickoutAlt,
    InscalarEventPageView,
    InscalarEventPageViewAlt,
} from '@appTypes/InscalarEvents.interface'

import useAppQueryState from '@hooks/useAppQueryState'

import { useMinimumGuaranteedCreditLimitQuery } from '@queries/useMinimumGuaranteedCreditLimitQuery'

import { getInscalarAppEnv } from '@utils/getInscalarAppEnv'
import { inscalarTracker } from '@utils/inscalar'

import useFeatureFlags from './useFeatureFlags'

export const COOKIE_CONSENT_KEY = 'cookieyes-consent'

const getResultsDisplayed = ({
    products,
    highlightProduct,
    numberOfCardsToShow,
}: {
    highlightProduct: ScoredCreditCardProduct | null
    numberOfCardsToShow?: number
    products: ScoredCreditCardProduct[]
}) => {
    const resultsDisplayed: InscalarTrackedCard[] = products?.map(
        (product, index) =>
            mapCardScoredProductToInscalarTrackedProduct({
                product,
                index,
                numberOfCardsToShow,
            })
    )
    if (highlightProduct && resultsDisplayed) {
        const resultsDisplayedHighlight =
            mapCardScoredProductToInscalarTrackedProduct({
                product: highlightProduct,
                index: 0,
            })

        return [
            {
                ...resultsDisplayedHighlight,
                display_order: 0,
            },
            ...resultsDisplayed,
        ]
    }

    return resultsDisplayed
}

const useInscalarTrackEvent = () => {
    const { featureFlags } = useFeatureFlags()
    const { track } = useInscalar()
    const { appQueryState } = useAppQueryState()
    const { data: minimumCreditLimit } = useMinimumGuaranteedCreditLimitQuery({
        customerId: appQueryState.customerId,
    })

    const trackBase = useCallback(
        (eventType: InscalarEventType, data?: InscalarEventBase) => {
            const outcome = data?.postSubmit?.outcome

            const payload: InscalarTrackPayload = {
                event_type: eventType,
                env: getInscalarAppEnv(),
                client: 'web',
                filters: {
                    cookie_consents: Cookies.get(COOKIE_CONSENT_KEY),
                    experiment_ids:
                        getFeatureFlagsForEvent(featureFlags)?.split(';') ?? [],
                },

                journey: data?.journey,
                journey_category: data?.journeyCategory,

                original_journey: data?.originalJourney,
                original_journey_category: data?.originalJourneyCategory,

                step: data?.step,
                previous_step: data?.previousStep,

                post_submit: {
                    outcome: {
                        credit_limit_requested: outcome?.creditLimitRequested,
                        results: outcome?.results,
                        results_displayed: outcome?.resultsDisplayed,
                        results_sorting: outcome?.resultsSorting,
                    },
                    product_click: data?.postSubmit?.productClick,
                },
            }

            track(
                'frontend_event',
                '8.0',
                {},
                payload as unknown as Record<string, unknown>
            )
        },
        [featureFlags, track]
    )

    const trackPageView = useCallback(
        ({
            step,
            journey,
            journeyCategory,
            products,
            resultsSorting,
            highlightProduct,
            numberOfCardsToShow,
        }: InscalarEventPageView) => {
            const previousStep = inscalarTracker.getPreviousStepForEvent(step)

            const originalJourney = inscalarTracker.getOriginalJourneyForEvent(
                journey,
                journeyCategory
            )

            const resultsDisplayed = getResultsDisplayed({
                products,
                highlightProduct,
                numberOfCardsToShow,
            })

            trackBase('pageview', {
                journey,
                journeyCategory,

                originalJourney: originalJourney.journey,
                originalJourneyCategory: originalJourney.journeyCategory,

                step,
                previousStep,
                postSubmit: {
                    outcome: {
                        creditLimitRequested:
                            minimumCreditLimit?.minimumGuaranteedCreditLimit!,
                        resultsDisplayed,
                        resultsSorting: resultsSorting,
                        results: resultsDisplayed.length,
                    },
                },
            })
        },
        [minimumCreditLimit?.minimumGuaranteedCreditLimit, trackBase]
    )

    const trackPageViewAlt = useCallback(
        ({
            step,
            journey,
            journeyCategory,
            products,
        }: InscalarEventPageViewAlt) => {
            const previousStep = inscalarTracker.getPreviousStepForEvent(step)
            const originalJourney = inscalarTracker.getOriginalJourneyForEvent(
                journey,
                journeyCategory
            )
            const resultsDisplayed = products?.map((product, index) =>
                mapCardAltProductToInscalarTrackedProduct({ product, index })
            )

            trackBase('pageview', {
                journey,
                journeyCategory,

                originalJourney: originalJourney.journey,
                originalJourneyCategory: originalJourney.journeyCategory,

                step,
                previousStep,
                postSubmit: {
                    outcome: {
                        creditLimitRequested:
                            minimumCreditLimit?.minimumGuaranteedCreditLimit!,
                        resultsDisplayed: resultsDisplayed,
                        results: resultsDisplayed.length,
                        resultsSorting: undefined,
                    },
                },
            })
        },
        [minimumCreditLimit?.minimumGuaranteedCreditLimit, trackBase]
    )

    const trackAccept = useCallback(
        ({
            step,
            journey,
            journeyCategory,
        }: {
            journey: 'credit_cards' | 'personal_loans'
            journeyCategory: 'credit_cards' | 'loans'
            step: 'credit_cards_scoring' | 'personal_loans_scoring'
        }) => {
            const previousStep = inscalarTracker.getPreviousStepForEvent(step)
            const originalJourney = inscalarTracker.getOriginalJourneyForEvent(
                journey,
                journeyCategory
            )

            trackBase('accept', {
                journey,
                journeyCategory,

                originalJourney: originalJourney.journey,
                originalJourneyCategory: originalJourney.journeyCategory,

                step,
                previousStep,
            })
        },
        [trackBase]
    )

    const trackSubmit = useCallback(
        ({
            step,
            journey,
            journeyCategory,
        }: {
            journey: 'credit_cards'
            journeyCategory: 'credit_cards'
            step: 'credit_cards_search'
        }) => {
            const previousStep = inscalarTracker.getPreviousStepForEvent(step)
            const originalJourney = inscalarTracker.getOriginalJourneyForEvent(
                journey,
                journeyCategory
            )

            trackBase('submit', {
                journey,
                journeyCategory,

                originalJourney: originalJourney.journey,
                originalJourneyCategory: originalJourney.journeyCategory,

                step,
                previousStep,
            })
        },
        [trackBase]
    )

    const trackDecline = useCallback(
        ({
            step,
            journey,
            journeyCategory,
        }: {
            journey: 'credit_cards' | 'personal_loans'
            journeyCategory: 'credit_cards' | 'loans'
            step: 'credit_cards_scoring' | 'personal_loans_scoring'
        }) => {
            const previousStep = inscalarTracker.getPreviousStepForEvent(step)
            const originalJourney = inscalarTracker.getOriginalJourneyForEvent(
                journey,
                journeyCategory
            )

            trackBase('decline', {
                journey,
                journeyCategory,

                originalJourney: originalJourney.journey,
                originalJourneyCategory: originalJourney.journeyCategory,

                step,
                previousStep,
            })
        },
        [trackBase]
    )

    const trackAction = useCallback(
        ({ step, journey, journeyCategory }: InscalarEventAction) => {
            const previousStep = inscalarTracker.getPreviousStepForEvent(step)
            const originalJourney = inscalarTracker.getOriginalJourneyForEvent(
                journey,
                journeyCategory
            )
            trackBase('action', {
                journey,
                journeyCategory,

                originalJourney: originalJourney.journey,
                originalJourneyCategory: originalJourney.journeyCategory,

                step,
                previousStep,
            })
        },
        [trackBase]
    )

    const trackClickoutAlt = useCallback(
        ({
            step,
            journey,
            journeyCategory,
            product,
            productPosition,
        }: InscalarEventClickoutAlt) => {
            const previousStep = inscalarTracker.getPreviousStepForEvent(step)
            const originalJourney = inscalarTracker.getOriginalJourneyForEvent(
                journey,
                journeyCategory
            )
            trackBase('clickout', {
                journey,
                journeyCategory,

                originalJourney: originalJourney.journey,
                originalJourneyCategory: originalJourney.journeyCategory,

                step,
                previousStep,

                postSubmit: {
                    productClick: mapCardAltProductToInscalarTrackedProduct({
                        product,
                        index: productPosition ?? 0,
                    }),
                },
            })
        },
        [trackBase]
    )

    const trackClickout = useCallback(
        ({
            step,
            journey,
            journeyCategory,
            product,
            productPosition,
        }: InscalarEventClickout) => {
            const previousStep = inscalarTracker.getPreviousStepForEvent(step)
            const originalJourney = inscalarTracker.getOriginalJourneyForEvent(
                journey,
                journeyCategory
            )
            trackBase('clickout', {
                journey,
                journeyCategory,

                originalJourney: originalJourney.journey,
                originalJourneyCategory: originalJourney.journeyCategory,

                step,
                previousStep,

                postSubmit: {
                    productClick: mapCardScoredProductToInscalarTrackedProduct({
                        product: product,
                        index: productPosition ?? 0,
                    }),
                },
            })
        },
        [trackBase]
    )

    return {
        trackBase,
        trackPageViewAlt,
        trackPageView,
        trackAccept,
        trackSubmit,
        trackDecline,
        trackAction,
        trackClickout,
        trackClickoutAlt,
    }
}

export default useInscalarTrackEvent
